import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  FileText,
  Plus,
  Edit2,
  Trash2,
  Copy,
  Eye,
  Save,
  X,
  Check,
  History,
  Code,
  Palette,
  Layout,
  RefreshCw,
  Star,
  StarOff,
  ChevronRight,
  Printer,
  FileDown,
  Users,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_TYPE_LABELS = {
  investor_statement: 'Investor Statement',
  client_statement: 'Client Statement',
  welcome_letter: 'Welcome Letter',
  welcome_ptc_client: 'Welcome — PTC Client',
  welcome_ptc_investor: 'Welcome — PTC Investor',
  welcome_ptam_investor: 'Welcome — PTAM Investor',
  distribution_notice: 'Distribution Notice',
  withdrawal_confirmation: 'Withdrawal Confirmation',
  tax_certificate: 'Tax Certificate',
  general_letter: 'General Letter',
  email_notification: 'Email Notification'
};

const TEMPLATE_TYPE_COLORS = {
  investor_statement: 'bg-blue-100 text-blue-700',
  client_statement: 'bg-green-100 text-green-700',
  welcome_letter: 'bg-purple-100 text-purple-700',
  welcome_ptc_client: 'bg-purple-100 text-purple-700',
  welcome_ptc_investor: 'bg-purple-100 text-purple-700',
  welcome_ptam_investor: 'bg-fuchsia-100 text-fuchsia-700',
  distribution_notice: 'bg-amber-100 text-amber-700',
  withdrawal_confirmation: 'bg-red-100 text-red-700',
  tax_certificate: 'bg-gray-100 text-gray-700',
  general_letter: 'bg-slate-100 text-slate-700',
  email_notification: 'bg-indigo-100 text-indigo-700'
};

export default function AdminTemplatesPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // State
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [templateVariables, setTemplateVariables] = useState({});
  const [variableGroups, setVariableGroups] = useState({});
  const [welcomeShortcodeGroups, setWelcomeShortcodeGroups] = useState({});
  const [welcomeShortcodesFlat, setWelcomeShortcodesFlat] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [shortcodeSearch, setShortcodeSearch] = useState('');
  const [selectedType, setSelectedType] = useState('');
  
  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    template_type: '',
    description: '',
    html_content: '',
    css_styles: '',
    header_html: '',
    footer_html: '',
    is_default: false,
    is_active: true
  });
  const [activeTab, setActiveTab] = useState('html');
  const [previewHtml, setPreviewHtml] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Version history
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  
  // Generate statement state
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateTemplate, setGenerateTemplate] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [selectedInvestor, setSelectedInvestor] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [generatedStatement, setGeneratedStatement] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Sample preview state
  const [showSamplePreview, setShowSamplePreview] = useState(false);
  const [samplePreviewHtml, setSamplePreviewHtml] = useState('');
  const [samplePreviewing, setSamplePreviewing] = useState(false);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/templates?is_active=true`, { headers });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch template types
  const fetchTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/templates/types`, { headers });
      setTemplateTypes(response.data.types || []);
      setTemplateVariables(response.data.variables || {});
      setVariableGroups(response.data.variable_groups || {});
    } catch (error) {
      console.error('Error fetching types:', error);
    }
  }, [token]);

  // Fetch welcome-email shortcodes (superset — used when template_type
  // begins with 'welcome_'). Backend enumerates every entity in
  // company_entities so brand-prefixed aliases (ptc_*, ptam_*) are up
  // to date without a code change.
  const fetchWelcomeShortcodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/welcome-email/shortcodes`, { headers });
      setWelcomeShortcodeGroups(response.data.groups || {});
      setWelcomeShortcodesFlat(response.data.flat || []);
    } catch (error) {
      console.error('Error fetching welcome shortcodes:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchTemplates();
    fetchTypes();
    fetchWelcomeShortcodes();
  }, [fetchTemplates, fetchTypes, fetchWelcomeShortcodes]);

  // Create new template
  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      template_type: '',
      description: '',
      html_content: '',
      css_styles: '',
      header_html: '',
      footer_html: '',
      is_default: false,
      is_active: true
    });
    setPreviewHtml('');
    setActiveTab('html');
    setShowEditor(true);
  };

  // Edit template
  const handleEdit = async (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      template_type: template.template_type,
      description: template.description || '',
      html_content: template.html_content || '',
      css_styles: template.css_styles || '',
      header_html: template.header_html || '',
      footer_html: template.footer_html || '',
      is_default: template.is_default || false,
      is_active: template.is_active !== false
    });
    setPreviewHtml('');
    setActiveTab('html');
    setShowEditor(true);
  };

  // Load starter template
  const loadStarterTemplate = async (type) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/templates/default/${type}`, { headers });
      if (response.data.starter_template) {
        const starter = response.data.starter_template;
        setFormData(prev => ({
          ...prev,
          template_type: type,
          name: starter.name || prev.name,
          html_content: starter.html_content || '',
          css_styles: starter.css_styles || '',
          header_html: starter.header_html || '',
          footer_html: starter.footer_html || ''
        }));
        toast.success('Starter template loaded');
      }
    } catch (error) {
      console.error('Error loading starter:', error);
    }
  };

  // Preview template
  const handlePreview = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/templates/preview`, {
        html_content: formData.html_content,
        css_styles: formData.css_styles
      }, { headers });
      setPreviewHtml(response.data.rendered_html);
      setActiveTab('preview');
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    }
  };

  // Save template
  const handleSave = async () => {
    if (!formData.name || !formData.template_type || !formData.html_content) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API_URL}/api/admin/templates/${editingTemplate.id}`, formData, { headers });
        toast.success('Template updated');
      } else {
        await axios.post(`${API_URL}/api/admin/templates`, formData, { headers });
        toast.success('Template created');
      }
      setShowEditor(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Duplicate template
  const handleDuplicate = async (template) => {
    try {
      await axios.post(`${API_URL}/api/admin/templates/${template.id}/duplicate`, {}, { headers });
      toast.success('Template duplicated');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  // Delete template
  const handleDelete = async (template) => {
    if (!window.confirm(`Delete "${template.name}"?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/templates/${template.id}`, { headers });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  // View version history
  const handleViewVersions = async (template) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/templates/${template.id}/versions`, { headers });
      setVersions(response.data.versions || []);
      setEditingTemplate(template);
      setShowVersions(true);
    } catch (error) {
      toast.error('Failed to load versions');
    }
  };

  // Open generate statement dialog
  const handleOpenGenerate = async (template) => {
    setGenerateTemplate(template);
    setSelectedInvestor('');
    setGeneratedStatement(null);
    
    // Set default period to last month
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
    setPeriodStart(lastMonth.toISOString().split('T')[0]);
    setPeriodEnd(lastDay.toISOString().split('T')[0]);
    
    // Fetch investors
    try {
      const response = await axios.get(`${API_URL}/api/admin/templates/${template.id}/investors`, { headers });
      setInvestors(response.data.investors || []);
    } catch (error) {
      console.error('Error fetching investors:', error);
    }
    
    setShowGenerateDialog(true);
  };

  // Generate statement
  const handleGenerateStatement = async () => {
    if (!selectedInvestor) {
      toast.error('Please select an investor');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/templates/${generateTemplate.id}/generate`,
        {
          investor_id: selectedInvestor,
          period_start: periodStart,
          period_end: periodEnd
        },
        { headers }
      );
      setGeneratedStatement(response.data);
      toast.success('Statement generated');
    } catch (error) {
      console.error('Error generating statement:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate statement');
    } finally {
      setGenerating(false);
    }
  };

  // Print statement
  const handlePrintStatement = () => {
    if (!generatedStatement?.rendered_html) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generatedStatement.rendered_html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Generate sample preview for a template
  const handleSamplePreview = async (template) => {
    setSamplePreviewing(true);
    setSamplePreviewHtml('');
    setShowSamplePreview(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/templates/preview`, {
        html_content: template.html_content,
        css_styles: template.css_styles
      }, { headers });
      setSamplePreviewHtml(response.data.rendered_html);
    } catch (error) {
      console.error('Error generating sample preview:', error);
      toast.error('Failed to generate sample preview');
      setShowSamplePreview(false);
    } finally {
      setSamplePreviewing(false);
    }
  };

  // Print sample preview
  const handlePrintSamplePreview = () => {
    if (!samplePreviewHtml) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(samplePreviewHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  // Insert variable at cursor
  const editorRef = React.useRef(null);
  const insertVariable = (variable) => {
    const textarea = editorRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const current = formData.html_content;
      const newContent = current.substring(0, start) + variable + current.substring(end);
      setFormData(prev => ({ ...prev, html_content: newContent }));
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    } else {
      setFormData(prev => ({ ...prev, html_content: prev.html_content + variable }));
    }
  };

  // Send a live test to the admin's own inbox — the ONLY reliable way
  // to eyeball shortcode + CSS rendering exactly as an investor would
  // receive it. Uses the currently-saved template row (not the
  // in-editor draft) so admins should press Save first if they've
  // edited. Test flag is prepended to the subject + a yellow banner
  // is injected server-side so the recipient can't confuse it with a
  // real welcome email.
  const [testSending, setTestSending] = useState(false);
  const handleSendTestToMe = async (template) => {
    const email = window.prompt(
      'Send test welcome email to which address?\n\nLeave blank to send to your own admin account.',
      ''
    );
    if (email === null) return;  // cancelled
    setTestSending(true);
    try {
      const body = { template_id: template.id };
      if (email.trim()) body.recipient_email = email.trim();
      const response = await axios.post(
        `${API_URL}/api/admin/welcome-email/send-test`,
        body,
        { headers },
      );
      toast.success(response.data?.message || `Test sent to ${email || 'your admin account'}`);
    } catch (error) {
      console.error('Send test failed:', error);
      // 424 = email service unreachable but the template rendered fine
      // — pop up the rendered HTML in a new tab so the admin can still
      // inspect it visually.
      const detail = error.response?.data?.detail;
      if (error.response?.status === 424 && detail && typeof detail === 'object' && detail.rendered_html_preview) {
        try {
          const w = window.open('', '_blank');
          if (w) {
            w.document.write(detail.rendered_html_preview);
            w.document.close();
          }
        } catch (_) { /* popup blocked — swallow */ }
        toast.warning(
          `Email service unavailable — preview opened in a new tab instead. (${detail.error || 'unknown'})`,
          { duration: 8000 }
        );
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.error || 'Failed to send test email');
        toast.error(msg);
      }
    } finally {
      setTestSending(false);
    }
  };

  // Toggle group expansion in shortcode panel
  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // Shortcode reference panel — filtered by type + search.
  // For welcome_* templates we swap to the welcome-email shortcode set
  // (fetched separately) because it enumerates brand-prefixed aliases
  // (ptc_*, ptam_*) from live company_entities data.
  const getRelevantGroups = () => {
    const isWelcome = (formData.template_type || '').startsWith('welcome_');
    const groupsSource = isWelcome ? welcomeShortcodeGroups : variableGroups;
    const flatWelcome = new Set(welcomeShortcodesFlat);
    const typeVars = new Set(templateVariables[formData.template_type] || []);
    const search = shortcodeSearch.toLowerCase();
    return Object.entries(groupsSource).map(([groupName, vars]) => {
      const filtered = Object.entries(vars).filter(([code, desc]) => {
        const matchesSearch = !search || code.toLowerCase().includes(search) || (desc || '').toLowerCase().includes(search);
        let relevantForType;
        if (isWelcome) {
          // Every welcome shortcode is relevant for every welcome_* type.
          relevantForType = flatWelcome.has(code) || !formData.template_type;
        } else {
          relevantForType = !formData.template_type || typeVars.has(code);
        }
        return matchesSearch && (relevantForType || search);
      });
      return { groupName, vars: filtered };
    }).filter(g => g.vars.length > 0);
  };

  // Filter templates by type
  const filteredTemplates = selectedType 
    ? templates.filter(t => t.template_type === selectedType)
    : templates;

  if (loading && templates.length === 0) {
    return (
      <div className="space-y-6" data-testid="templates-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-templates-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Document Templates
          </h1>
          <p className="text-gray-500 mt-1">
            Manage templates for statements, letters, and notifications
          </p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-amber-500 hover:bg-amber-600 text-slate-900"
          data-testid="create-template-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Type Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={!selectedType ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedType('')}
            >
              All ({templates.length})
            </Button>
            {templateTypes.map(type => {
              const count = templates.filter(t => t.template_type === type).length;
              return (
                <Button
                  key={type}
                  variant={selectedType === type ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedType(type)}
                  className={selectedType === type ? '' : TEMPLATE_TYPE_COLORS[type]}
                >
                  {TEMPLATE_TYPE_LABELS[type] || type} ({count})
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTemplates.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="p-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No templates found</p>
              <Button onClick={handleCreate} variant="outline" className="mt-4">
                Create Your First Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredTemplates.map(template => (
            <Card key={template.id} className={`relative ${!template.is_active ? 'opacity-50' : ''}`}>
              {template.is_default && (
                <div className="absolute top-2 right-2">
                  <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                </div>
              )}
              <CardHeader className="pb-2">
                <div className="flex items-start gap-2">
                  <Badge className={TEMPLATE_TYPE_COLORS[template.template_type]}>
                    {TEMPLATE_TYPE_LABELS[template.template_type] || template.template_type}
                  </Badge>
                </div>
                <CardTitle className="text-lg mt-2">{template.name}</CardTitle>
                {template.description && (
                  <CardDescription>{template.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="text-xs text-gray-500 mb-3">
                  Version {template.version || 1} • Updated {new Date(template.updated_at).toLocaleDateString()}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(template)}>
                    <Edit2 className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  {(template.template_type === 'investor_statement' || template.template_type === 'distribution_notice') && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleOpenGenerate(template)}
                      className="bg-green-50 hover:bg-green-100 text-green-700"
                    >
                      <FileDown className="h-3 w-3 mr-1" /> Generate
                    </Button>
                  )}
                  {(template.template_type === 'investor_statement' || template.template_type === 'client_statement') && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSamplePreview(template)}
                      className="bg-blue-50 hover:bg-blue-100 text-blue-700"
                      data-testid={`sample-preview-btn-${template.id}`}
                    >
                      <Eye className="h-3 w-3 mr-1" /> Sample Preview
                    </Button>
                  )}
                  {(template.template_type || '').startsWith('welcome_') && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSendTestToMe(template)}
                      disabled={testSending}
                      className="bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-200"
                      data-testid={`send-test-welcome-btn-${template.id}`}
                      title="Send this welcome email to yourself (or another address) to verify shortcode + CSS rendering before sending to investors"
                    >
                      <Send className="h-3 w-3 mr-1" /> {testSending ? 'Sending…' : 'Send Test to Me'}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => handleDuplicate(template)}>
                    <Copy className="h-3 w-3 mr-1" /> Copy
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleViewVersions(template)}>
                    <History className="h-3 w-3 mr-1" /> History
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(template)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Template Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
            <DialogDescription>
              Design your document template using HTML and CSS
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col gap-4">
            {/* Template Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Template Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Monthly Investor Statement"
                  data-testid="template-name"
                />
              </div>
              <div>
                <Label>Type *</Label>
                <Select
                  value={formData.template_type}
                  onValueChange={(v) => {
                    setFormData(p => ({ ...p, template_type: v }));
                    if (!formData.html_content) {
                      loadStarterTemplate(v);
                    }
                  }}
                >
                  <SelectTrigger data-testid="template-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {templateTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {TEMPLATE_TYPE_LABELS[type] || type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData(p => ({ ...p, is_default: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Default template</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(p => ({ ...p, is_active: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>
            </div>

            {/* Editor Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
              <TabsList>
                <TabsTrigger value="html" className="flex items-center gap-1">
                  <Code className="h-4 w-4" /> HTML Content
                </TabsTrigger>
                <TabsTrigger value="css" className="flex items-center gap-1">
                  <Palette className="h-4 w-4" /> CSS Styles
                </TabsTrigger>
                <TabsTrigger value="header" className="flex items-center gap-1">
                  <Layout className="h-4 w-4" /> Header/Footer
                </TabsTrigger>
                <TabsTrigger value="preview" className="flex items-center gap-1">
                  <Eye className="h-4 w-4" /> Preview
                </TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-hidden mt-4">
                <TabsContent value="html" className="h-full m-0">
                  <div className="grid grid-cols-4 gap-4 h-full">
                    <div className="col-span-3">
                      <Textarea
                        ref={editorRef}
                        value={formData.html_content}
                        onChange={(e) => setFormData(p => ({ ...p, html_content: e.target.value }))}
                        placeholder="Enter HTML content..."
                        className="h-[400px] font-mono text-sm"
                        data-testid="html-content"
                      />
                    </div>
                    {/* === SHORTCODE REFERENCE PANEL === */}
                    <div className="border rounded-lg overflow-hidden flex flex-col h-[400px]" data-testid="shortcode-panel">
                      <div className="px-3 py-2 bg-gray-50 border-b shrink-0">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Shortcode Reference</p>
                        <input
                          type="text"
                          placeholder="Search..."
                          value={shortcodeSearch}
                          onChange={e => setShortcodeSearch(e.target.value)}
                          className="w-full text-xs px-2 py-1 border rounded focus:outline-none focus:border-amber-400"
                          data-testid="shortcode-search"
                        />
                        {formData.template_type && !shortcodeSearch && (
                          <p className="text-xs text-amber-600 mt-1">Showing vars for current type</p>
                        )}
                        {shortcodeSearch && (
                          <p className="text-xs text-blue-600 mt-1">Searching all shortcodes</p>
                        )}
                      </div>
                      <div className="overflow-y-auto flex-1">
                        {getRelevantGroups().map(({ groupName, vars }) => (
                          <div key={groupName} className="border-b last:border-0">
                            <button
                              onClick={() => toggleGroup(groupName)}
                              className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 bg-gray-50/80"
                            >
                              <span>{groupName}</span>
                              <span className="text-gray-400">{expandedGroups[groupName] === false ? '+' : '−'}</span>
                            </button>
                            {expandedGroups[groupName] !== false && (
                              <div className="divide-y divide-gray-50">
                                {vars.map(([code, desc]) => (
                                  <div
                                    key={code}
                                    className="group relative flex items-start gap-1 pr-2"
                                    title={desc}
                                  >
                                    <button
                                      onClick={() => insertVariable(code)}
                                      className="block flex-1 text-left px-3 py-1.5 hover:bg-amber-50 transition-colors"
                                      data-testid={`shortcode-insert-${code.replace(/[{}]/g,'')}`}
                                    >
                                      <span className="font-mono text-blue-700 text-xs block">{code}</span>
                                      <span className="text-gray-400 text-xs truncate block max-w-full">{desc}</span>
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigator.clipboard.writeText(code);
                                        toast.success(`Copied ${code}`);
                                      }}
                                      className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-amber-600 py-1.5"
                                      title="Copy shortcode"
                                      data-testid={`shortcode-copy-${code.replace(/[{}]/g,'')}`}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                        {getRelevantGroups().length === 0 && (
                          <p className="text-xs text-gray-400 p-3">No shortcodes found</p>
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="css" className="h-full m-0">
                  <Textarea
                    value={formData.css_styles}
                    onChange={(e) => setFormData(p => ({ ...p, css_styles: e.target.value }))}
                    placeholder="Enter CSS styles..."
                    className="h-[400px] font-mono text-sm"
                    data-testid="css-styles"
                  />
                </TabsContent>

                <TabsContent value="header" className="h-full m-0 space-y-4">
                  <div>
                    <Label>Header HTML</Label>
                    <Textarea
                      value={formData.header_html}
                      onChange={(e) => setFormData(p => ({ ...p, header_html: e.target.value }))}
                      placeholder="Header content (appears at top of document)..."
                      className="h-[150px] font-mono text-sm"
                    />
                  </div>
                  <div>
                    <Label>Footer HTML</Label>
                    <Textarea
                      value={formData.footer_html}
                      onChange={(e) => setFormData(p => ({ ...p, footer_html: e.target.value }))}
                      placeholder="Footer content (appears at bottom of document)..."
                      className="h-[150px] font-mono text-sm"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="preview" className="h-full m-0">
                  <div className="border rounded-lg h-[400px] overflow-auto bg-white">
                    {previewHtml ? (
                      <iframe
                        srcDoc={previewHtml}
                        title="Template Preview"
                        className="w-full h-full"
                        sandbox="allow-same-origin"
                      />
                    ) : (
                      <div className="h-full flex items-center justify-center text-gray-500">
                        <div className="text-center">
                          <Eye className="h-12 w-12 mx-auto mb-2 opacity-30" />
                          <p>Click "Preview" to see your template</p>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancel
            </Button>
            <Button variant="outline" onClick={handlePreview}>
              <Eye className="h-4 w-4 mr-2" /> Preview
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              {saving ? 'Saving...' : (
                <>
                  <Save className="h-4 w-4 mr-2" /> Save Template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={showVersions} onOpenChange={setShowVersions}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription>
              {editingTemplate?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[400px] overflow-y-auto">
            {versions.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No version history</p>
            ) : (
              <div className="space-y-2">
                {versions.map((v, idx) => (
                  <div key={v.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Version {v.version}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(v.saved_at).toLocaleString()}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          await axios.post(
                            `${API_URL}/api/admin/templates/${editingTemplate.id}/restore/${v.version}`,
                            {},
                            { headers }
                          );
                          toast.success(`Restored to version ${v.version}`);
                          setShowVersions(false);
                          fetchTemplates();
                        } catch (error) {
                          toast.error('Failed to restore version');
                        }
                      }}
                    >
                      Restore
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVersions(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Statement Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="h-5 w-5" />
              Generate Statement
            </DialogTitle>
            <DialogDescription>
              {generateTemplate?.name} - Select an investor and period
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            {!generatedStatement ? (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <Label>Select Investor *</Label>
                    <Select value={selectedInvestor} onValueChange={setSelectedInvestor}>
                      <SelectTrigger data-testid="generate-investor">
                        <SelectValue placeholder="Choose investor" />
                      </SelectTrigger>
                      <SelectContent>
                        {investors.map(inv => (
                          <SelectItem key={inv.id} value={inv.id}>
                            {inv.name} ({inv.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Period Start</Label>
                    <Input
                      type="date"
                      value={periodStart}
                      onChange={(e) => setPeriodStart(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Period End</Label>
                    <Input
                      type="date"
                      value={periodEnd}
                      onChange={(e) => setPeriodEnd(e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="flex justify-center pt-4">
                  <Button
                    onClick={handleGenerateStatement}
                    disabled={generating || !selectedInvestor}
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                  >
                    {generating ? 'Generating...' : 'Generate Statement'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500">Investor</p>
                    <p className="font-medium">{generatedStatement.investor_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Period</p>
                    <p className="font-medium">{generatedStatement.period}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Portfolio Value</p>
                    <p className="font-medium">R {generatedStatement.data_summary?.portfolio_value}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Active Loans</p>
                    <p className="font-medium">{generatedStatement.data_summary?.active_loans}</p>
                  </div>
                </div>
                
                {/* Preview */}
                <div className="border rounded-lg overflow-hidden" style={{ height: '400px' }}>
                  <iframe
                    srcDoc={generatedStatement.rendered_html}
                    title="Statement Preview"
                    className="w-full h-full"
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            {generatedStatement ? (
              <>
                <Button variant="outline" onClick={() => setGeneratedStatement(null)}>
                  Back
                </Button>
                <Button variant="outline" onClick={handlePrintStatement}>
                  <Printer className="h-4 w-4 mr-2" /> Print / Save PDF
                </Button>
                <Button onClick={() => setShowGenerateDialog(false)}>
                  Done
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
                Cancel
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sample Preview Dialog */}
      <Dialog open={showSamplePreview} onOpenChange={setShowSamplePreview}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="sample-preview-dialog">
          <DialogHeader>
            <DialogTitle>Sample Statement Preview</DialogTitle>
            <DialogDescription>
              Preview with sample data — this is how the template will look when rendered
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            {samplePreviewing ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="h-6 w-6 animate-spin text-gray-400 mr-2" />
                <span className="text-gray-500">Generating preview...</span>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden" style={{ height: '60vh' }}>
                <iframe
                  srcDoc={samplePreviewHtml}
                  title="Sample Statement Preview"
                  className="w-full h-full"
                  sandbox="allow-same-origin"
                  data-testid="sample-preview-iframe"
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handlePrintSamplePreview} disabled={samplePreviewing || !samplePreviewHtml}>
              <Printer className="h-4 w-4 mr-2" /> Print / Save PDF
            </Button>
            <Button onClick={() => setShowSamplePreview(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
